import React from 'react';
import './styles/About.css';

import endResult1 from './../assets/endKnife1.webp';
import endResult2 from './../assets/endKnife2.webp';

import SEO from "../seo/SEO";
import config from "../config/config";
import generateBreadcrumbs from '../utility/generateBreadcrumbs';

import exoticWood from './../assets/exoticWood.webp';

const OurProcess = () => {

    const processJsonLd = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "Knife-Making Process - Withrow Knives",
        "description": "A step-by-step overview of how Withrow Knives are made, from raw steel shaping to handle design and custom leather sheath crafting.",
        "url": `${config.metadata.siteUrl}/our-process`,
        "image": `${config.metadata.siteUrl}/logo512.png`,
        "breadcrumb": generateBreadcrumbs(["our-process"]),
        "step": [
            {
                "@type": "HowToStep",
                "name": "Step 1: Preparing the Steel",
                "text": "Each knife begins as high-quality steel, carefully selected for durability and performance. The raw material is cut and prepared before shaping begins."
            },
            {
                "@type": "HowToStep",
                "name": "Step 2: Forging & Shaping the Blade",
                "text": "The steel is heated, hammered, and shaped into the desired blade design with precision for balance and strength."
            },
            {
                "@type": "HowToStep",
                "name": "Step 3: Crafting the Handle",
                "text": "Exotic woods are hand-selected for their durability and beauty. Each handle is carefully shaped, polished, and fitted to complement the blade."
            },
            {
                "@type": "HowToStep",
                "name": "Step 4: Handcrafting the Leather Sheath",
                "text": "Each knife is paired with a custom leather sheath, hand-cut, stamped, and sewn to ensure a perfect fit and long-lasting protection."
            },
            {
                "@type": "HowToStep",
                "name": "Final Step: The Finished Masterpiece",
                "text": "The end result is a beautifully crafted knife—designed for durability, functionality, and aesthetic appeal."
            }
        ],
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${config.metadata.siteUrl}/our-process`
        }
    };


    return (
        <>
            <SEO
                title="Our Knife-Making Process - Withrow Knives"
                description="Discover Withrow Knives' handcrafted process—from raw steel selection to forging, handle crafting, and custom leather sheaths."
                keywords={[
                    "knife making",
                    "handcrafted knives",
                    "forging knives",
                    "custom knives",
                    "handmade knives",
                    "leather sheaths"
                ]}
                url={`${config.metadata.siteUrl}/our-process`}
                jsonLd={processJsonLd}
            />

            <div className='page-wrapper'>
                {/* ✅ Page Title with Background */}
                <div 
                    className='page-title' 
                    style={{ backgroundImage: `url(${exoticWood})`, backgroundSize: "cover", backgroundPosition: "50% 80%" }}
                >
                    <h1>OUR PROCESS</h1>
                    <p className="page-subtitle">Find out how your knife is made.</p>
                </div>

                {/* ✅ Main About Content Section */}
                <section className='page-content-section'>

                    {/* ✅ The Process Section */}
                    <div className="process-section" aria-labelledby="process-overview">
                        <h2 id='process-overview'>The Process</h2>
                        <p>
                            Every Withrow knife is meticulously handcrafted through a multi-step process, ensuring precision, durability, and craftsmanship at every stage. 
                            From selecting premium steel to the final finishing touches, each knife undergoes careful refinement to meet the highest standards.
                        </p>


                        {/* 🔹 Step 1: Raw Materials */}
                        <article className="process-step left">
                            <img src={require("./../assets/babyKnives.webp")} alt="Raw metal before shaping" aria-hidden="true"/>
                            <div className="step-text">
                                <h3>Preparing the Steel</h3>
                                <p>Each knife begins as high-quality steel, carefully selected for durability and performance. The raw material is cut and prepared before the shaping process begins.</p>
                            </div>
                        </article>

                        {/* 🔹 Step 2: Shaping the Blade */}
                        <article className="process-step right">
                            <div className="step-text">
                                <h3>Forging & Shaping the Blade</h3>
                                <p>The steel is heated, hammered, and shaped into the desired blade design. Each knife is crafted with precision to ensure balance and strength.</p>
                            </div>
                            <img src={require("./../assets/kidKnives.webp")} alt="Shaped blades before handle attachment" aria-hidden="true"/>
                        </article>

                        {/* 🔹 Step 3: Crafting the Handle */}
                        <article className="process-step left">
                            <img src={require("./../assets/exoticWood.webp")} alt="Exotic wood for knife handles" aria-hidden="true"/>
                            <div className="step-text">
                                <h3>Crafting the Handle</h3>
                                <p>Exotic woods are hand-selected for their durability and beauty. Each handle is carefully shaped, polished, and fitted to complement the blade.</p>
                            </div>
                        </article>

                        {/* 🔹 Step 4: Creating the Sheath */}
                        <article className="process-step right">
                            <div className="step-text">
                                <h3>Handcrafting the Leather Sheath</h3>
                                <p>Each knife is paired with a custom leather sheath, hand-cut, stamped, and sewn to ensure a perfect fit and long-lasting protection.</p>
                            </div>
                            <img src={require("./../assets/customSheaths.webp")} alt="Custom-made leather sheaths" aria-hidden="true"/>
                        </article>
                    </div>

                    {/* ✅ Final Product Section */}
                    <div className="final-product-section" aria-labelledby="final-product-title">
                        <h2 id="final-product-title">The Finished Masterpiece</h2>
                        <p>
                            The end result is a beautifully crafted knife—designed for durability, functionality, and aesthetic appeal. 
                            Whether collected as a keepsake or used for everyday tasks, a Withrow knife is a testament to the skill, passion, 
                            and dedication behind every step of the process. The attention to detail in each stage—from shaping the blade to 
                            hand-finishing the handle and crafting a custom sheath—is reflected in the final product, ensuring both quality 
                            and longevity.
                        </p>


                        {/* 🔹 Display End Product Images */}
                        <div className="final-product-images">
                            <img src={endResult1} alt="Finished custom knife" />
                            <img src={endResult2} alt="Another finished custom knife" />
                        </div>
                    </div>


                </section>
            </div>
        </>
    );
};

export default OurProcess;
